/**
 * `beginLogin()` — kick off the Cognito hosted-UI flow under the
 * cookie-auth deployment.
 *
 * The colocation infra (Bifrost #87) routes `/login` through the
 * furnace-router ALB with an `OnUnauthenticatedRequest: authenticate`
 * listener rule. Visiting `/login` therefore:
 *
 *   1. Hits the ALB
 *   2. ALB sees no session cookie → redirects to Cognito hosted UI
 *   3. User signs in (or, if already signed in to Cognito, the page
 *      bounces back immediately)
 *   4. Cognito redirects back to `/oauth2/idpresponse?code=...&state=...`
 *      on the same origin
 *   5. ALB validates the code, sets `AWSELBAuthSessionCookie-*` on
 *      `foundry-staging.weekend.com`
 *   6. Browser is at `/`, cookie carries identity on every subsequent
 *      request
 *
 * No PKCE flow in the SPA. No code exchange. No token storage. No
 * service worker. The legacy `auth/` PKCE module was removed when
 * cookie-mode became canonical.
 *
 * The function is wrapped behind a tiny dependency-injection seam so
 * unit tests can pin the redirect without navigating the test
 * document.
 */

export interface LoginDependencies {
  /** Top-level navigation. Real impl is `window.location.assign`. */
  redirect: (url: string) => void;
}

/**
 * Default dependencies — real browser globals. Real callers pass
 * this; tests construct their own mock.
 */
function defaultLoginDependencies(): LoginDependencies {
  return {
    redirect: (url) => {
      window.location.assign(url);
    },
  };
}

/**
 * Begin the Cognito login flow by navigating the top-level
 * document to `/login`. Returns `never` because the assign unloads
 * the document — there is no "after the navigation" code path.
 *
 * The return-to-path round-trip is currently NOT implemented here.
 * The ALB's `/login` listener rule's RedirectConfig (or a wrapping
 * route on the SPA's hash router) is the right place to preserve
 * the deep-link target; the v2 doc has this as an open question
 * (see the bifrost#87 v3 follow-up review). For v1, login always
 * lands the user back at `/`.
 */
export function beginLogin(deps: LoginDependencies = defaultLoginDependencies()): never {
  deps.redirect('/login');
  // Unreachable in real browsers — the assign unloads the document.
  // The throw guarantees the type-level `never` return for callers.
  throw new Error('beginLogin: unreachable — top-level redirect must unload');
}
